import { useCallback, useEffect, useState } from 'react'

type Params = {
  /**
   * 表示する結果画像を識別するキー（画像 ID など）。
   * 署名付き URL はデータを取り直すたびに変わるため、「原寸を読み込み済みか」は URL ではなくこのキーで覚える
   */
  imageKey: string | undefined
  /** 表示用プレビュー（縮小 JPEG）の URL。未生成なら空 */
  previewUrl: string | undefined
  /** 原寸の URL */
  url: string | undefined
  /** false なら段階表示せず最初から原寸を返す（編集モードなど、原寸の naturalWidth/Height が要るとき） */
  enabled: boolean
}

type Result = {
  /** `<img src>` に渡す URL */
  src: string | undefined
  /** `src` が原寸かどうか。false の間は画像の naturalWidth/Height を原寸として扱ってはいけない */
  isFullRes: boolean
  /**
   * `<img>` が原寸（`isFullRes === true`）を読み込み終えたときに呼ぶ。
   * 段階表示が無効（編集モード）で原寸を直接表示していた画像を「読み込み済み」として覚え、
   * あとで段階表示を有効にしたとき（閲覧モードへ戻したとき）にプレビューへ戻さないため
   */
  onFullResLoad: () => void
}

/**
 * 結果画像の段階表示（issue #2466）。
 *
 * 先に軽いプレビューを表示し、原寸を背景で読み込み終えたら差し替える。
 * プレビューが無い・段階表示が無効・その画像の原寸を一度読み込み済み、のいずれかなら原寸を返す。
 * 原寸の読み込みに失敗したときはプレビューのまま（DL 系は本 hook を通らず原寸 URL を使う）。
 * 同じ画像のまま URL だけ変わった（署名の更新）ときは、プレビューに戻さず新しい原寸 URL を返す。
 * 段階表示が無効な間に `<img>` が原寸を読み込み終えたら `onFullResLoad` で知らせてもらい、
 * 有効に切り替わったときもその画像はプレビューに戻さない。
 */
export const useProgressiveImageSrc = ({ imageKey, previewUrl, url, enabled }: Params): Result => {
  const [loadedFullFor, setLoadedFullFor] = useState<string | undefined>(undefined)
  // キーが無いと「読み込み済み」を覚えられないので段階表示しない（原寸をそのまま返す）
  const shouldStage =
    enabled && imageKey !== undefined && Boolean(previewUrl) && Boolean(url) && previewUrl !== url
  const fullLoaded = imageKey !== undefined && loadedFullFor === imageKey

  const onFullResLoad = useCallback(() => {
    if (imageKey !== undefined) {
      setLoadedFullFor(imageKey)
    }
  }, [imageKey])

  useEffect(() => {
    if (!shouldStage || fullLoaded || !url || imageKey === undefined) {
      return undefined
    }
    let cancelled = false
    const image = new Image()
    image.onload = () => {
      if (!cancelled) {
        setLoadedFullFor(imageKey)
      }
    }
    image.src = url
    return () => {
      cancelled = true
      image.onload = null
    }
  }, [shouldStage, fullLoaded, url, imageKey])

  if (!shouldStage || fullLoaded) {
    return { src: url, isFullRes: true, onFullResLoad }
  }
  return { src: previewUrl, isFullRes: false, onFullResLoad }
}
